import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { COLORS } from '@nzassa/shared';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('[NzassaLearn] Uncaught error:', error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const t = i18n.t.bind(i18n);

      return (
        <div className="flex min-h-screen items-center justify-center bg-[#F7FAFC] px-4" role="alert">
          <div className="max-w-md text-center space-y-6">
            <img
              src="/logos/nzassalearn-compact.svg"
              alt="NzassaLearn"
              className="mx-auto h-12 w-12"
            />
            <h1 className="text-xl font-bold" style={{ color: COLORS.ink }}>
              {t('errors.erreur_survenue')}
            </h1>
            <p className="text-sm text-slate-500">
              {t('errors.probleme_inattendu')}
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-4 max-h-40 overflow-auto rounded bg-slate-100 p-3 text-left text-xs text-red-600">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-2 rounded-lg border-2 px-5 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ borderColor: COLORS.blue, color: COLORS.blue }}
              >
                {t('errors.reessayer')}
              </button>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ backgroundColor: COLORS.blue }}
              >
                {t('errors.retour_accueil')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
